import React, { useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import PersonalDetails from "../stepper/Personal Details/PersonalDetails";
import OfficialInformation from "../stepper/OfficialInformation/OfficialInformation";
import PriceList from "../stepper/PriceInfo/PriceList";
import PaymentPlan from "../stepper/PaymentPlan/PaymentPlan";
import ProjectMedia from "../stepper/ProjectMedia/ProjectMedia";
import Seo from "../stepper/Seo/Seo";
import { openErrorNotification } from "../../../../helpers/methods";
import urls from "../../../../global/constants/UrlConstants";

const stepData = [
  {
    label: "Project Information",
    hint: "Basic project details",
  },
  {
    label: "Project Listing",
    hint: "Listing & property facts",
  },
  {
    label: "Unit Config",
    hint: "Unit types & pricing",
  },
  {
    label: "Payment Plan",
    hint: "Optional — payment stages & plans",
  },
  {
    label: "Project Media",
    hint: "Images & documents",
  },
  {
    label: "SEO",
    hint: "Meta title & description",
  },
];

interface CustomProps {
  isDialogOpen?: boolean;
  handleCloseAddTeam: Function;
  userId: string;
  idd: string;
  setIdd: Function;
  fromSettingOrLogout?: string;
  tableDataShowHandler: Function;
  data?: any;
  mode?: "page" | "dialog";
}

const AddNewTeam = (props: CustomProps) => {
  const history = useHistory();
  const location = useLocation();
  const params = useParams<{ id?: string }>();
  const pathId =
    params?.id ||
    location.pathname.match(/\/project\/create\/([^/?#]+)/)?.[1] ||
    "";
  const projectId = String(props.idd || pathId || "");
  const isEdit = Boolean(projectId);

  const [activeStep, setActiveStep] = useState<number>(0);
  const [completed, setCompleted] = useState<{ [k: number]: boolean }>({});

  const handleClose = () => {
    props.tableDataShowHandler?.();
    if (props.mode === "page") {
      history.push(urls.PROJECTS);
    } else {
      props.handleCloseAddTeam();
    }
  };

  const nextStep = (next = activeStep) => {
    if (next >= stepData.length - 1) {
      setCompleted((prev) => ({ ...prev, [next]: true }));
      handleClose();
      return;
    }
    setCompleted((prev) => ({ ...prev, [next]: true }));
    setActiveStep(next + 1);
  };

  const backStep = () => {
    setActiveStep((s) => Math.max(0, s - 1));
  };

  const handleStepClick = (index: number) => {
    try {
      if (!props.userId?.length) {
        throw new Error("Please Register user");
      }
      if (index < 0 || index >= stepData.length) return;
      // With an existing project, allow jumping to any tab
      if (projectId || index <= activeStep || completed[index]) {
        setActiveStep(index);
      } else if (index === activeStep + 1) {
        setActiveStep(index);
      }
    } catch {
      openErrorNotification("  Please Register  user");
    }
  };

  const getAllContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <PersonalDetails
            userId={props.userId}
            nextStep={nextStep}
            idd={projectId}
            data={props.data}
            setIdd={props.setIdd}
            fromSettingOrLogout={props.fromSettingOrLogout}
          />
        );
      case 1:
        return (
          <OfficialInformation
            userId={props.userId}
            nextStep={nextStep}
            backStep={backStep}
            idd={projectId}
            data={props.data}
            fromSettingOrLogout={props.fromSettingOrLogout}
          />
        );
      case 2:
        return (
          <PriceList
            userId={props.userId}
            nextStep={nextStep}
            backStep={backStep}
            idd={projectId}
            fromSettingOrLogout={props.fromSettingOrLogout}
          />
        );
      case 3:
        return (
          <PaymentPlan
            userId={props.userId}
            nextStep={nextStep}
            backStep={backStep}
            idd={projectId}
            fromSettingOrLogout={props.fromSettingOrLogout}
          />
        );
      case 4:
        return (
          <ProjectMedia
            userId={props.userId}
            nextStep={nextStep}
            backStep={backStep}
            idd={projectId}
            data={props.data}
            handleClose={handleClose}
            fromSettingOrLogout={props.fromSettingOrLogout}
          />
        );
      case 5:
        return (
          <Seo
            userId={props.userId}
            nextStep={nextStep}
            backStep={backStep}
            idd={projectId}
            setIdd={props.setIdd}
            data={props.data}
            handleClose={handleClose}
            fromSettingOrLogout={props.fromSettingOrLogout}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-[#F7F8FC]">
      <div className="flex shrink-0 items-start justify-between gap-4 border-b border-[#EEF0F5] bg-white px-4 py-4 sm:px-6">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-[#111827] sm:text-2xl">
            {isEdit ? "Edit Project" : "Create New Projects"}
          </h1>
          <p className="mt-0.5 text-sm text-[#6B7280]">
            {isEdit
              ? "Update the project details below"
              : "Fill in the details below to create a new project"}
          </p>
        </div>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-hidden p-4 lg:grid-cols-[260px_minmax(0,1fr)] lg:gap-5 lg:p-6">
        <aside className="shrink-0 overflow-y-auto rounded-2xl border border-[#EEF0F5] bg-white p-5">
          <ol className="relative space-y-0">
            {stepData.map((step, index) => {
              const isActive = index === activeStep;
              const isDone = index < activeStep || completed[index];
              const isLast = index === stepData.length - 1;
              return (
                <li key={step.label} className="relative flex gap-3 pb-6 last:pb-0">
                  {!isLast ? (
                    <span
                      className={`absolute left-[15px] top-8 h-[calc(100%-16px)] w-0.5 ${
                        isDone ? "bg-[#A02448]" : "bg-[#E5E7EB]"
                      }`}
                    />
                  ) : null}
                  <button
                    type="button"
                    onClick={() => handleStepClick(index)}
                    className="relative z-[1] flex w-full items-start gap-3 text-left"
                  >
                    <span
                      className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                        isActive
                          ? "bg-[#A02448] text-white ring-4 ring-[#EEF0FF]"
                          : isDone
                            ? "bg-[#A02448] text-white"
                            : "bg-[#F3F4F6] text-[#6B7280]"
                      }`}
                    >
                      {isDone && !isActive
                        ? "✓"
                        : String(index + 1).padStart(2, "0")}
                    </span>
                    <span
                      className={`min-w-0 flex-1 rounded-xl px-3 py-2 transition ${
                        isActive ? "bg-[#EEF0FF]" : "hover:bg-[#F8FAFC]"
                      }`}
                    >
                      <span
                        className={`block text-sm font-semibold leading-snug ${
                          isActive ? "text-[#A02448]" : "text-[#111827]"
                        }`}
                      >
                        {step.label}
                      </span>
                      <span className="mt-0.5 block text-xs text-[#9CA3AF]">
                        {step.hint}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </aside>

        <main className="min-h-0 min-w-0 overflow-y-auto rounded-2xl border border-[#EEF0F5] bg-white p-4 sm:p-6">
          {getAllContent(activeStep)}
        </main>
      </div>
    </div>
  );
};

export default AddNewTeam;
